import React from "react";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import { Skeleton } from "primereact/skeleton";
import PropTypes from "prop-types";
import ReactCountryFlag from "react-country-flag";
import CustomCheckBox from "../../../../components/CheckBox/index";

const UserAccesTable = ({
  accesslists,
  paginationOnchange,
  paginationFisrt,
  paginationRows,
  hanleChangeUpdateAccess,
  handleBatchToggle,
  updatEntity,
  role,
  allTenantData,
  loading,
}) => {
  const isGxmAdmin = role === "GXM_ADMIN" || role === "SUPER_ADMIN";
  const isCustomer = role === "CUSTOMER" || role === "CUSTOMER_MANAGER";

  const isProcessChecked = (entityId, serviceId, processId) => {
    const match = updatEntity?.find(
      (x) =>
        x.entityId == entityId &&
        x.serviceId == serviceId &&
        x.processId == processId,
    );
    return match ? match.checked : true;
  };

  const isServiceChecked = (entity, service) => {
    return service.process_templates?.every((process) =>
      isProcessChecked(entity.id, service.id, process.id),
    );
  };

  const hasAnyServiceProcessChecked = (entity, service) => {
    return service.process_templates?.some((process) =>
      isProcessChecked(entity.id, service.id, process.id),
    );
  };

  const isEntityChecked = (entity) => {
    return entity.services?.every((service) =>
      isServiceChecked(entity, service),
    );
  };

  const hasAnyEntityProcessChecked = (entity) => {
    return entity.services?.some((service) =>
      hasAnyServiceProcessChecked(entity, service),
    );
  };

  const toggleEntity = (entity, checked) => {
    const items = [];
    entity.services?.forEach((service) => {
      service.process_templates?.forEach((process) => {
        items.push({
          entityId: entity.id,
          serviceId: service.id,
          processId: process.id,
          checked,
        });
      });
    });
    handleBatchToggle(items);
  };

  const toggleService = (entity, service, checked) => {
    const items = [];
    service.process_templates?.forEach((process) => {
      items.push({
        entityId: entity.id,
        serviceId: service.id,
        processId: process.id,
        checked,
      });
    });
    handleBatchToggle(items);
  };

  const getCheckState = (allChecked, anyChecked) => {
    if (allChecked) return "checked";
    if (anyChecked) return "indeterminate";
    return "unchecked";
  };

  const buildEntityNode = (entity) => {
    const entityChecked = isEntityChecked(entity);
    const entityHasAnyChecked = hasAnyEntityProcessChecked(entity);
    const entityState = getCheckState(entityChecked, entityHasAnyChecked);
    return {
      key: `entity-${entity.id}`,
      data: {
        name: (
          <span className="inline-flex align-items-center">
            <CustomCheckBox
              inputId={`checkbox-entity-${entity.id}`}
              checked={entityState !== "unchecked"}
              className={
                entityState === "indeterminate" ? "p-indeterminate" : ""
              }
              onChange={() => toggleEntity(entity, entityState !== "checked")}
            />
            <label
              className="ml-2 inline-flex align-items-center"
              htmlFor={`checkbox-entity-${entity.id}`}
            >
              <ReactCountryFlag
                countryCode={entity.country}
                svg
                className="mr-2"
              />
              {`${entity.name} (${entity.country})`}
            </label>
          </span>
        ),
      },
      children: entity.services?.map((service) => {
        const serviceChecked = isServiceChecked(entity, service);
        const serviceHasAnyChecked = hasAnyServiceProcessChecked(
          entity,
          service,
        );
        const serviceState = getCheckState(
          serviceChecked,
          serviceHasAnyChecked,
        );

        return {
          key: `service-${service.id}`,
          data: {
            name: (
              <span className="inline-flex align-items-center">
                <CustomCheckBox
                  inputId={`checkbox-service-${entity.id}-${service.id}`}
                  checked={serviceState !== "unchecked"}
                  className={
                    serviceState === "indeterminate" ? "p-indeterminate" : ""
                  }
                  onChange={() =>
                    toggleService(entity, service, serviceState !== "checked")
                  }
                />
                <label
                  className="ml-2"
                  htmlFor={`checkbox-service-${entity.id}-${service.id}`}
                >
                  {service.name}
                </label>
              </span>
            ),
          },
          children: service.process_templates?.map((process) => {
            const isChecked = isProcessChecked(
              entity.id,
              service.id,
              process.id,
            );
            return {
              key: `process-${process.id}`,
              data: {
                name: (
                  <span className="inline-flex align-items-center">
                    <CustomCheckBox
                      inputId={`checkbox-${entity.id}-${service.id}-${process.id}`}
                      checked={isChecked}
                      onChange={(e) =>
                        hanleChangeUpdateAccess(
                          e.target.checked,
                          entity.id,
                          service.id,
                          process.id,
                        )
                      }
                    />
                    <label
                      className="ml-2"
                      htmlFor={`checkbox-${entity.id}-${service.id}-${process.id}`}
                    >
                      {process.name}
                    </label>
                  </span>
                ),
              },
            };
          }),
        };
      }),
    };
  };

  const convertToTreeNodes = (data) => {
    // Customer User/Manager: flat entity list (no tenant grouping)
    // Entity → Service → Process Templates
    if (isCustomer) {
      return data.map((entity) => buildEntityNode(entity));
    }

    // Group entities by tenant
    const tenantMap = {};

    data.forEach((entity) => {
      const tId = entity.tenant_id || "unknown";
      const tenantData = entity.tenantData || {
        id: tId,
        tenant_name: "Unknown Tenant",
      };

      if (!tenantMap[tId]) {
        tenantMap[tId] = {
          id: tId,
          name: tenantData.tenant_name || "Unknown Tenant",
          entities: [],
        };
      }
      tenantMap[tId].entities.push(entity);
    });

    // GXM Admin / Super Admin: show tenant list from allTenantData
    if (isGxmAdmin) {
      if (allTenantData && allTenantData.length > 0) {
        return allTenantData.map((tenant) => ({
          key: `tenant-${tenant.id}`,
          data: {
            name: <span>{tenant.name}</span>,
          },
        }));
      }
      // Fallback: derive from entity access if allTenantData is not available
      return Object.values(tenantMap).map((tenant) => ({
        key: `tenant-${tenant.id}`,
        data: {
          name: <span>{tenant.name}</span>,
        },
      }));
    }

    // LSP User/Manager & GXM User/Manager: full tree Tenant → Entity → Service → Process Templates
    return Object.values(tenantMap).map((tenant) => ({
      key: `tenant-${tenant.id}`,
      data: {
        name: <span>{tenant.name}</span>,
      },
      children: tenant.entities.map((entity) => buildEntityNode(entity)),
    }));
  };

  const skeletonNodes = [
    {
      key: "skeleton-0",
      data: {
        name: (
          <div>
            <Skeleton width="60%" height="1.2rem" />
          </div>
        ),
      },
    },
  ];

  const treeNodes = loading
    ? skeletonNodes
    : convertToTreeNodes(accesslists || []);

  return (
    <div
      className={`user-access-table${isGxmAdmin ? " user-access-table--gxm-admin" : ""}`}
    >
      <TreeTable
        value={treeNodes}
        emptyMessage="No results were found"
        responsiveLayout="scroll"
        paginator={!loading && accesslists?.length > 10}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        rowsPerPageOptions={[10, 20, 30]}
        first={paginationFisrt}
        rows={paginationRows}
        onPage={paginationOnchange}
      >
        <Column
          field="name"
          header={
            isGxmAdmin ? (
              <span>
                Account Name{" "}
                <i
                  className="pi pi-info-circle info-icon"
                  id="gxm-admin-access-info"
                />
                <Tooltip
                  target="#gxm-admin-access-info"
                  content="GXM Admin users inherit full visibility across all entities, processes, and services within the tenant."
                  position="right"
                  className="gxm-admin-tooltip"
                />
              </span>
            ) : (
              "Account Name"
            )
          }
          expander={!isGxmAdmin}
          className="table-entity-column"
        />
      </TreeTable>
    </div>
  );
};

UserAccesTable.propTypes = {
  accesslists: PropTypes.array,
  paginationOnchange: PropTypes.func,
  paginationFisrt: PropTypes.number,
  paginationRows: PropTypes.number,
  hanleChangeUpdateAccess: PropTypes.func,
  handleBatchToggle: PropTypes.func,
  updatEntity: PropTypes.array,
  role: PropTypes.string,
  allTenantData: PropTypes.array,
  loading: PropTypes.bool,
};

export default UserAccesTable;
